import {
    LexModelsV2Client,
    ListSessionMetricsCommand,
    ListUtteranceMetricsCommand
} from '@aws-sdk/client-lex-models-v2';
import { requestQueueManager } from './requestQueueManager';

// Route analytics calls through the shared rate limiter's dedicated 'lexAnalytics' bucket.
// The Lex V2 analytics sub-API (ListSessionMetrics/ListUtteranceMetrics) has a much lower
// throttle quota than the model-building List/Describe APIs, so it gets its own slow,
// serialized bucket — and benefits from the circuit breaker / backoff like every other call.
const analyticsSend = (client, command) =>
  requestQueueManager.executeRequest(() => client.send(command), { resourceType: 'lexAnalytics' });

// Build a single reusable analytics client (the caller creates one and passes it to every
// fetchLexV2Analytics call, rather than constructing a fresh client per request).
export const createLexAnalyticsClient = (credentials, region) => new LexModelsV2Client({
  region,
  credentials: {
    accessKeyId: credentials.accessKeyId,
    secretAccessKey: credentials.secretAccessKey,
    sessionToken: credentials.sessionToken,
  },
  retryMode: 'adaptive', // SDK adaptive retry as a backstop; requestQueueManager is primary pacing
  maxAttempts: 2,
});

export const fetchLexV2Analytics = async (client, botId, aliasId = 'TSTALIASID') => {
  const endTime = new Date();
  const startTime = new Date();
  startTime.setDate(startTime.getDate() - 30); // Last 30 days

  console.log(`Fetching Lex V2 analytics for bot ID: ${botId}, alias: ${aliasId}`);

  try {
    // Fetch session metrics (conversations) - CORRECTED STRUCTURE
    const sessionMetricsCommand = new ListSessionMetricsCommand({
      botId: botId,
      startDateTime: startTime,
      endDateTime: endTime,
      filters: [
        {
          name: 'BotAliasId',
          operator: 'EQ',
          values: [aliasId]
        }
      ],
      metrics: [
        {
          name: 'Count',
          statistic: 'Sum'
        },
        {
          name: 'Success',
          statistic: 'Sum'
        },
        {
          name: 'Failure',
          statistic: 'Sum'
        },
        {
          name: 'Dropped',
          statistic: 'Sum'
        }
      ]
    });

    // Fetch utterance metrics - CORRECTED STRUCTURE
    const utteranceMetricsCommand = new ListUtteranceMetricsCommand({
      botId: botId,
      startDateTime: startTime,
      endDateTime: endTime,
      filters: [
        {
          name: 'BotAliasId',
          operator: 'EQ',
          values: [aliasId]
        }
      ],
      metrics: [
        {
          name: 'Count',
          statistic: 'Sum'
        },
        {
          name: 'Detected',
          statistic: 'Sum'
        },
        {
          name: 'Missed',
          statistic: 'Sum'
        }
      ]
    });

    // Issue both calls through the rate limiter. The 'lexAnalytics' bucket serializes them
    // (concurrency 1, ~1 req/s) so this fan-out can't outrun the analytics API's low quota.
    const sessionResponse = await analyticsSend(client, sessionMetricsCommand);
    const utteranceResponse = await analyticsSend(client, utteranceMetricsCommand);

    // Process session metrics
    let totalConversations = 0;
    let successfulConversations = 0;
    let failedConversations = 0;
    let droppedConversations = 0;

    if (sessionResponse.results && sessionResponse.results.length > 0) {
      const result = sessionResponse.results[0];
      if (result.metricsResults) {
        result.metricsResults.forEach(metric => {
          const value = Math.round(metric.value || 0);
          switch (metric.name) {
            case 'Count':
              totalConversations = value;
              break;
            case 'Success':
              successfulConversations = value;
              break;
            case 'Failure':
              failedConversations = value;
              break;
            case 'Dropped':
              droppedConversations = value;
              break;
            default:
              // Unknown metric type, skip
              break;
          }
        });
      }
    }

    // Process utterance metrics
    let totalUtterances = 0;
    let detectedUtterances = 0;
    let missedUtterances = 0;

    if (utteranceResponse.results && utteranceResponse.results.length > 0) {
      const result = utteranceResponse.results[0];
      if (result.metricsResults) {
        result.metricsResults.forEach(metric => {
          const value = Math.round(metric.value || 0);
          switch (metric.name) {
            case 'Count':
              totalUtterances = value;
              break;
            case 'Detected':
              detectedUtterances = value;
              break;
            case 'Missed':
              missedUtterances = value;
              break;
            default:
              // Unknown metric type, skip
              break;
          }
        });
      }
    }

    const analytics = {
      totalConversations,
      successfulConversations,
      failedConversations,
      droppedConversations,
      totalUtterances,
      detectedUtterances,
      missedUtterances
    };

    console.log(`Analytics for bot ${botId}:`, analytics);
    return analytics;

  } catch (error) {
    console.warn(`Failed to fetch Lex V2 analytics for bot ${botId}:`, error);
    return {
      totalConversations: 0,
      successfulConversations: 0,
      failedConversations: 0,
      droppedConversations: 0,
      totalUtterances: 0,
      detectedUtterances: 0,
      missedUtterances: 0
    };
  }
};